import React, { useState, useRef, useEffect } from 'react';
import { UserCircle, Settings, LogOut, Palette, ChevronRight, Bell, Info, CheckCircle, AlertTriangle, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from './Theme';
import logo from "../../assets/sa_logo.svg"; 
type NotificationType = 'info' | 'success' | 'warning' | 'error';

interface NotificationItem {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  time: string;
  read?: boolean;
}

const NOTIFICATION_CONFIG: Record<NotificationType, { icon: React.ElementType; bgClass: string; iconClass: string; borderClass: string }> = {
  info: { icon: Info, bgClass: 'bg-blue-500/10', iconClass: 'text-blue-400', borderClass: 'border-l-blue-500' },
  success: { icon: CheckCircle, bgClass: 'bg-emerald-500/10', iconClass: 'text-emerald-400', borderClass: 'border-l-emerald-500' },
  warning: { icon: AlertTriangle, bgClass: 'bg-amber-500/10', iconClass: 'text-amber-400', borderClass: 'border-l-amber-500' },
  error: { icon: AlertCircle, bgClass: 'bg-red-500/10', iconClass: 'text-red-400', borderClass: 'border-l-red-500' },
};

// Sample notifications – replace with real data/API later
const SAMPLE_NOTIFICATIONS: NotificationItem[] = [
  { id: '1', type: 'success', title: 'Ingestion completed', message: 'Data model "Sales CRM" finished syncing successfully.', time: '2 min ago', read: false },
  { id: '2', type: 'warning', title: 'Schedule delayed', message: 'Nightly job was delayed by 15 minutes due to high load.', time: '1 hour ago', read: true },
  { id: '3', type: 'info', title: 'New update available', message: 'Portal update v2.4 is ready to install.', time: '3 hours ago', read: true },
  { id: '4', type: 'error', title: 'Connection failed', message: 'Database connection "Warehouse" timed out. Check credentials.', time: 'Yesterday', read: false },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const [themeNestedOpen, setThemeNestedOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const [notifications] = useState<NotificationItem[]>(SAMPLE_NOTIFICATIONS);
  const unreadCount = notifications.filter((n) => !n.read).length;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setNotifOpen(false);
    };
    if (notifOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [notifOpen]);

  const routeToLogin = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };
  const routeTohome = () => {
    navigate('/home')
  }
  const getUserFullName = () => {
    const user = localStorage.getItem('user_name');
    return user;
  }
  return (
    <header className={`w-full h-16 px-6 flex items-center justify-between fixed top-0 left-0 z-50 border-b ${theme === 'white' ? 'bg-white border-neutral-200 shadow-[0_1px_3px_rgba(0,0,0,0.06)]' : 'bg-neutral-900 border-neutral-800 shadow-[0_1px_3px_rgba(0,0,0,0.3)]'}`}>
      {/* Logo + Title */}
      <button
        type="button"
        onClick={routeTohome}
        className={`flex items-center gap-3 rounded-lg py-2 pr-2 -ml-2 text-left transition-colors focus:outline-none ${theme === 'white' ? 'hover:bg-neutral-100' : 'hover:bg-neutral-800/50'}`}
        aria-label="Go to home"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 64 64"
          width="28"
          height="28"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className={`shrink-0 ${theme === 'white' ? 'text-indigo-500' : 'text-indigo-400'}`}
          aria-hidden
        >
          <polygon points="32,4 56,18 56,46 32,60 8,46 8,18" strokeLinejoin="round" />
          <circle cx="32" cy="32" r="6" />
          <line x1="32" y1="26" x2="32" y2="12" strokeLinecap="round" />
          <line x1="38" y1="32" x2="50" y2="32" strokeLinecap="round" />
          <line x1="32" y1="38" x2="32" y2="52" strokeLinecap="round" />
          <line x1="26" y1="32" x2="14" y2="32" strokeLinecap="round" />
          <circle cx="32" cy="12" r="1.5" fill="currentColor" />
          <circle cx="32" cy="52" r="1.5" fill="currentColor" />
          <circle cx="14" cy="32" r="1.5" fill="currentColor" />
          <circle cx="50" cy="32" r="1.5" fill="currentColor" />
        </svg>
        {/* <img src={logo} alt="SA Logo" className="w-7 h-7 shrink-0" /> */}
        <span className={`text-lg font-semibold tracking-tight ${theme === 'white' ? 'text-neutral-900' : 'text-white'}`}>SelficAI</span>
      </button>

      {/* Notifications + User Menu */}
      <div className="flex items-center gap-2">
        {/* Notification bell */}
        <div className="relative" ref={notifRef}>
          <button
            type="button"
            onClick={() => { setNotifOpen(!notifOpen); setOpen(false); }}
            className={`relative p-2 rounded-lg focus:outline-none focus:ring-2 transition-colors ${theme === 'white' ? 'text-indigo-500 hover:text-indigo-600 hover:bg-indigo-50 focus:ring-indigo-200' : 'text-indigo-400 hover:text-white hover:bg-neutral-800 focus:ring-neutral-700'}`}
            aria-label="Notifications"
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold !text-white bg-indigo-500 rounded-full border-2 ${theme === 'white' ? 'border-white' : 'border-neutral-900 shadow-sm'}`}>
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>
          {notifOpen && (
            <div className={`absolute right-0 mt-2 w-[380px] max-h-[420px] flex flex-col rounded-xl shadow-xl z-50 overflow-hidden ring-1 ring-black/10 ${theme === 'white' ? 'bg-white border border-neutral-200' : 'bg-neutral-800 border border-neutral-700'}`}>
              <div className={`flex items-center justify-between px-4 py-3 border-b ${theme === 'white' ? 'border-neutral-200 bg-white' : 'border-neutral-700 bg-neutral-800/95'}`}>
                <h3 className={`text-sm font-semibold ${theme === 'white' ? 'text-neutral-900' : 'text-white'}`}>Notifications</h3>
                {unreadCount > 0 && (
                  <span className={`text-xs ${theme === 'white' ? 'text-neutral-500' : 'text-gray-400'}`}>{unreadCount} unread</span>
                )}
              </div>
              <div className="overflow-y-auto flex-1">
                {notifications.length === 0 ? (
                  <div className="px-4 py-8 text-center text-sm text-gray-500">No notifications yet</div>
                ) : (
                  <ul className="py-1">
                    {notifications.map((n) => {
                      const config = NOTIFICATION_CONFIG[n.type];
                      const Icon = config.icon;
                      return (
                        <li
                          key={n.id}
                          className={`flex gap-3 px-4 py-3 border-l-4 ${config.borderClass} ${n.read ? (theme === 'white' ? 'bg-neutral-50' : 'bg-neutral-800/50') : config.bgClass} ${theme === 'white' ? 'hover:bg-neutral-100' : 'hover:bg-neutral-700/50'} transition-colors cursor-pointer`}
                        >
                          <span className={`shrink-0 mt-0.5 ${config.iconClass}`}>
                            <Icon className="w-5 h-5" />
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className={`text-sm font-medium ${n.read ? (theme === 'white' ? 'text-neutral-600' : 'text-neutral-400') : (theme === 'white' ? 'text-neutral-900' : 'text-white')}`}>{n.title}</p>
                            <p className={`text-xs mt-0.5 line-clamp-2 ${theme === 'white' ? 'text-neutral-500' : 'text-neutral-400'}`}>{n.message}</p>
                            <p className={`text-[11px] mt-1 ${theme === 'white' ? 'text-neutral-400' : 'text-neutral-500'}`}>{n.time}</p>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              <div className={`px-4 py-2 border-t ${theme === 'white' ? 'border-neutral-200 bg-white' : 'border-neutral-700 bg-neutral-800/95'}`}>
                <button
                  type="button"
                  className={`w-full py-2 text-xs font-medium transition-colors ${theme === 'white' ? 'text-neutral-500 hover:text-neutral-900' : 'text-gray-400 hover:text-white'}`}
                >
                  View all notifications
                </button>
              </div>
            </div>
          )}
        </div>

        {/* User Menu */}
        <div className="relative">
          <button
            type="button"
            onClick={() => { setOpen(!open); setNotifOpen(false); }}
            className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg transition-colors focus:outline-none ${theme === 'white' ? 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100' : 'text-neutral-400 hover:text-white hover:bg-neutral-800/50'}`}
            aria-expanded={open}
            aria-haspopup="true"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`w-5 h-5 ${theme === 'white' ? 'text-neutral-500' : 'text-neutral-400'}`}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.6}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M16 21v-2a4 4 0 0 0-8 0v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
            {getUserFullName()}
            <svg
              className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Dropdown */}
          {open && (
            <div className={`absolute right-0 mt-2 w-52 rounded-xl shadow-xl z-50 py-1 ring-1 ring-black/10 ${theme === 'white' ? 'bg-white border border-neutral-200' : 'bg-neutral-800 border border-neutral-700'}`}>
              <a
                href="#"
                className={`flex items-center gap-2 px-4 py-2 text-sm ${theme === 'white' ? 'text-neutral-700 hover:bg-neutral-100' : 'text-gray-200 hover:bg-neutral-700'}`}
              >
                <UserCircle className="w-4 h-4" /> My Profile
              </a>
              <a
                href="#"
                className={`flex items-center gap-2 px-4 py-2 text-sm ${theme === 'white' ? 'text-neutral-700 hover:bg-neutral-100' : 'text-gray-200 hover:bg-neutral-700'}`}
              >
                <Settings className="w-4 h-4" /> Settings
              </a>
              {/* Nested Theme */}
              <div
                className="relative"
                onMouseEnter={() => setThemeNestedOpen(true)}
                onMouseLeave={() => setThemeNestedOpen(false)}
              >
                <button
                  type="button"
                  className={`flex items-center justify-between w-full gap-2 px-4 py-2 text-sm ${theme === 'white' ? 'text-neutral-700 hover:bg-neutral-100' : 'text-gray-200 hover:bg-neutral-700'}`}
                >
                  <span className="flex items-center gap-2">
                    <Palette className="w-4 h-4" /> Theme
                  </span>
                  <ChevronRight className="w-4 h-4 shrink-0" />
                </button>
                {themeNestedOpen && (
                  <div className={`absolute right-full top-0 mr-0.5 w-36 rounded-lg shadow-xl z-50 py-1 min-w-0 ring-1 ring-black/10 ${theme === 'white' ? 'bg-white border border-neutral-200' : 'bg-neutral-800 border border-neutral-700'}`}>
                    <button
                      type="button"
                      onClick={() => { setTheme('black'); setOpen(false); setThemeNestedOpen(false); }}
                      className={`flex items-center gap-2 w-full px-3 py-2 text-sm text-left ${theme === 'white' ? 'hover:bg-neutral-100 text-neutral-600' : 'hover:bg-neutral-700 text-white bg-neutral-700/50'}`}
                    >
                      <span className="w-3 h-3 rounded-full bg-neutral-800 border border-neutral-600 shrink-0" />
                      Black
                    </button>
                    <button
                      type="button"
                      onClick={() => { setTheme('white'); setOpen(false); setThemeNestedOpen(false); }}
                      className={`flex items-center gap-2 w-full px-3 py-2 text-sm text-left ${theme === 'white' ? 'hover:bg-neutral-100 text-neutral-900 bg-neutral-100' : 'hover:bg-neutral-700 text-gray-200'}`}
                    >
                      <span className="w-3 h-3 rounded-full bg-white border border-neutral-400 shrink-0" />
                      White
                    </button>
                  </div>
                )}
              </div>
              <a
                onClick={routeToLogin}
                className={`flex items-center gap-2 px-4 py-2 text-sm text-red-400 cursor-pointer ${theme === 'white' ? 'hover:bg-red-50' : 'hover:bg-neutral-700'}`}
              >
                <LogOut className="w-4 h-4" /> Logout
              </a>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
